import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabaseAuth } from '@/lib/supabase-auth'

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isValidToken, setIsValidToken] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // 检查URL中是否有重置token
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabaseAuth.auth.getSession()

        // 检查是否是recovery session
        if (session) {
          setIsValidToken(true)
        } else {
          // 没有有效的token，重定向到忘记密码页面
          setError('无效的重置链接，请重新发送重置邮件')
          setTimeout(() => {
            navigate('/forgot-password')
          }, 3000)
        }
      } catch (err) {
        console.error('Session check error:', err)
        setError('无效的重置链接')
        setTimeout(() => {
          navigate('/forgot-password')
        }, 3000)
      }
    }

    checkSession()
  }, [navigate])

  // 密码复杂度验证
  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return '密码至少需要 8 个字符'
    }
    if (!/[A-Z]/.test(pwd)) {
      return '密码必须包含至少一个大写字母'
    }
    if (!/[a-z]/.test(pwd)) {
      return '密码必须包含至少一个小写字母'
    }
    if (!/[0-9]/.test(pwd)) {
      return '密码必须包含至少一个数字'
    }
    return null
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // 验证密码
      const passwordError = validatePassword(password)
      if (passwordError) {
        throw new Error(passwordError)
      }

      if (password !== confirmPassword) {
        throw new Error('两次输入的密码不一致')
      }

      // 更新密码
      const { error: updateError } = await supabaseAuth.auth.updateUser({
        password: password,
      })

      if (updateError) throw updateError

      // 登出以确保用户使用新密码登录
      await supabaseAuth.auth.signOut()

      setSuccess(true)
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : '重置密码失败')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">密码重置成功！</h2>
            <p className="text-gray-600 mb-6">
              您的密码已成功重置，现在可以使用新密码登录。
            </p>
            <p className="text-sm text-gray-400">2 秒后自动跳转到登录页...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!isValidToken && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">验证重置链接...</h2>
            <p className="text-gray-600">请稍候</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src="https://flowspark.net/favicon.ico"
              alt="Logo"
              className="w-16 h-16 rounded-2xl shadow-lg"
            />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            API Adapter
          </h1>
          <p className="text-gray-600">设置新密码</p>
        </div>

        {/* Reset Password Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">重置密码</h2>
          <p className="text-sm text-gray-600 mb-6">请设置您的新密码</p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-5">
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                新密码
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                placeholder="至少8位，包含大小写字母和数字"
                required
                disabled={!isValidToken}
              />
              <p className="text-xs text-gray-500 mt-1">
                密码要求：至少8位，包含大写字母、小写字母和数字
              </p>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                确认新密码
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                placeholder="再次输入新密码"
                required
                disabled={!isValidToken}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !isValidToken}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              {loading ? '重置中...' : '重置密码'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-500 mt-8">
          © 2025 API Adapter. All rights reserved.
        </p>
      </div>
    </div>
  )
}
